import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Star as StarIcon,
  Share as ShareIcon,
  LocalOffer as LocalOfferIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import statsService from '../services/statsService';

function Statistics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await statsService.getGlobalStats();
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value || 0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box p={3}>
        <Typography variant="h5" color="error">
          Erreur lors du chargement des statistiques
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
        📊 Statistiques Globales
      </Typography>

      {/* Statistiques principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Utilisateurs */}
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'primary.dark', height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Utilisateurs
                  </Typography>
                  <Typography variant="h3">{stats.users.total}</Typography>
                  <Typography variant="caption" color="success.light">
                    +{stats.users.thisMonth} ce mois
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Commandes */}
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'success.dark', height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Commandes
                  </Typography>
                  <Typography variant="h3">{stats.orders.total}</Typography>
                  <Typography variant="caption" color="warning.light">
                    {stats.orders.paid} payées ({stats.orders.conversionRate}%)
                  </Typography>
                </Box>
                <ShoppingCartIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Chiffre d'affaires */}
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'warning.dark', height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Chiffre d'affaires
                  </Typography>
                  <Typography variant="h4">{formatCurrency(stats.revenue.total)}</Typography>
                  <Typography variant="caption" color="success.light">
                    {formatCurrency(stats.revenue.thisMonth)} ce mois
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Panier moyen */}
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'info.dark', height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Panier moyen
                  </Typography>
                  <Typography variant="h4">{formatCurrency(stats.revenue.avgOrderValue)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Par commande
                  </Typography>
                </Box>
                <LocalOfferIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section Utilisateurs & Fidélité */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        👥 Utilisateurs & Fidélité
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Statistiques Utilisateurs
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Total utilisateurs:
                </Typography>
                <Typography variant="h5">{stats.users.total}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Nouveaux ce mois:
                </Typography>
                <Typography variant="h5">{stats.users.thisMonth}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Avec commandes:
                </Typography>
                <Typography variant="h5">{stats.users.withOrders}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Taux de conversion:
                </Typography>
                <Typography variant="h5" color="success.main">{stats.users.conversionRate}%</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Fidélité Globale
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Points distribués:
                </Typography>
                <Typography variant="h5">{stats.loyalty.totalPoints.toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  XP distribué:
                </Typography>
                <Typography variant="h5">{stats.loyalty.totalXp.toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Niveau moyen:
                </Typography>
                <Typography variant="h5" color="warning.main">{stats.loyalty.avgLevel}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Section Parrainage */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        🎁 Système de Parrainage
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Total filleuls
              </Typography>
              <Typography variant="h4">{stats.referrals.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Filleuls validés
              </Typography>
              <Typography variant="h4" color="success.main">{stats.referrals.validated}</Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.referrals.validationRate}% de validation
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Points distribués
              </Typography>
              <Typography variant="h4">{stats.referrals.totalPoints.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                XP distribué
              </Typography>
              <Typography variant="h4">{stats.referrals.totalXp.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section Affiliation */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        💼 Programme d'Affiliation
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Affiliés actifs
              </Typography>
              <Typography variant="h4">{stats.affiliates.active} / {stats.affiliates.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Avis publiés
              </Typography>
              <Typography variant="h4">{stats.affiliates.publishedReviews} / {stats.affiliates.reviews}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Campagnes actives
              </Typography>
              <Typography variant="h4">{stats.affiliates.activeCampaigns} / {stats.affiliates.campaigns}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section Produits */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        📦 Produits & Stock
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Total produits
              </Typography>
              <Typography variant="h4">{stats.products.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'success.dark' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                En stock
              </Typography>
              <Typography variant="h4">{stats.products.inStock}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'error.dark' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Rupture de stock
              </Typography>
              <Typography variant="h4">{stats.products.outOfStock}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tables de classements */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Top Parrains */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center" gap={1}>
              <TrophyIcon /> Top Parrains
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nom</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">Filleuls</TableCell>
                    <TableCell align="right">Points</TableCell>
                    <TableCell align="right">XP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.referrals.topReferrers && stats.referrals.topReferrers.length > 0 ? (
                    stats.referrals.topReferrers.map((referrer, index) => (
                      <TableRow key={referrer.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {index === 0 && <Typography>🥇</Typography>}
                            {index === 1 && <Typography>🥈</Typography>}
                            {index === 2 && <Typography>🥉</Typography>}
                            {referrer.name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={referrer.code} size="small" color="primary" />
                        </TableCell>
                        <TableCell align="right">{referrer.totalReferrals}</TableCell>
                        <TableCell align="right">{referrer.points}</TableCell>
                        <TableCell align="right">{referrer.xp}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">Aucun parrain</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Top Utilisateurs par Niveau */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center" gap={1}>
              <StarIcon /> Top Niveaux
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Utilisateur</TableCell>
                    <TableCell align="right">Niveau</TableCell>
                    <TableCell align="right">XP</TableCell>
                    <TableCell align="right">Points</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.loyalty.topUsers && stats.loyalty.topUsers.length > 0 ? (
                    stats.loyalty.topUsers.slice(0, 5).map((user, index) => (
                      <TableRow key={user.userId}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {index === 0 && <Typography>🥇</Typography>}
                            {index === 1 && <Typography>🥈</Typography>}
                            {index === 2 && <Typography>🥉</Typography>}
                            <Box>
                              <Typography variant="body2">{user.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={`Niv. ${user.level}`} size="small" color="warning" />
                        </TableCell>
                        <TableCell align="right">{user.xp.toLocaleString()}</TableCell>
                        <TableCell align="right">{user.points.toLocaleString()}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">Aucun utilisateur</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Top Produits */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center" gap={1}>
              <InventoryIcon /> Top Produits Vendus
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produit</TableCell>
                    <TableCell align="right">Vendus</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.products.topSelling && stats.products.topSelling.length > 0 ? (
                    stats.products.topSelling.map((product, index) => (
                      <TableRow key={product.productId}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {index === 0 && <Typography>🥇</Typography>}
                            {index === 1 && <Typography>🥈</Typography>}
                            {index === 2 && <Typography>🥉</Typography>}
                            {product.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={product.totalSold} size="small" color="success" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        <Typography color="text.secondary">Aucune vente</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Statistiques détaillées */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              📈 Détails Avancés
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Paniers actifs (7j):
                </Typography>
                <Typography variant="h6">{stats.carts.active}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Valeur paniers:
                </Typography>
                <Typography variant="h6">{formatCurrency(stats.carts.totalValue)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Taux abandon:
                </Typography>
                <Typography variant="h6" color="error.main">{stats.carts.abandonmentRate}%</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Promos actives:
                </Typography>
                <Typography variant="h6">{stats.promotions.active}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Évolution des commandes (30 derniers jours) */}
      {stats.evolution.last30Days && stats.evolution.last30Days.length > 0 && (
        <>
          <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
            📊 Évolution (30 derniers jours)
          </Typography>
          <Paper sx={{ p: 3, mb: 4 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Commandes</TableCell>
                    <TableCell align="right">Chiffre d'affaires</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.evolution.last30Days.slice(-10).map((day) => (
                    <TableRow key={day.date}>
                      <TableCell>
                        {new Date(day.date).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell align="right">{day.orders}</TableCell>
                      <TableCell align="right">{formatCurrency(day.revenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Résumé général */}
      <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
        <Typography variant="h6" gutterBottom>
          📋 Résumé Général
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              CA total / Panier moyen:
            </Typography>
            <Typography variant="body1">
              {formatCurrency(stats.revenue.total)} / {formatCurrency(stats.revenue.avgOrderValue)}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Utilisateurs actifs / Total:
            </Typography>
            <Typography variant="body1">
              {stats.users.withOrders} / {stats.users.total} ({stats.users.conversionRate}%)
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Parrainage - Validés / Total:
            </Typography>
            <Typography variant="body1">
              {stats.referrals.validated} / {stats.referrals.total} ({stats.referrals.validationRate}%)
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default Statistics;

